import type { Effect } from '../view/renderer';

/**
 * 이펙트 큐 상한. 섭취 이펙트(good/bad)에 걸리는 FIFO 상한이고, 재배치 링 16장은 이 상한을
 * 넘어 들어올 수 있다 — 대신 링은 **전부 아니면 전무**다(SEAL §5.E · ADR-008).
 *
 * 왜 링을 따로 다루는가: 타일 배열은 정답 타일부터 slot 오름차순이라, 링 16장을 FIFO 로 밀면
 * 밀려나는 첫 장이 항상 다음 정답 타일이다(60판 76,422틱 실측에서 100%). 링이 붙은 집합과 안 붙은
 * 집합이 갈리는 순간 "링 없는 타일 = 정답" 이 되어 정답 정보가 이펙트 큐로 샌다.
 */
export const EFFECT_MAX = 12;

export function isRing(fx: Effect): boolean {
  return fx.kind === 'relocate';
}

/**
 * 재배치 링 전체 집합. 세션은 이 배열로 큐를 **통째로** 갈아끼운다 — 링을 한 장씩 push 하지 않는다.
 */
export function ringEffects(cells: readonly number[], ttl: number): Effect[] {
  return cells.map((cell) => ({ kind: 'relocate' as const, cell, ttl }));
}

/**
 * 순수 함수 — 입력 배열은 건드리지 않고 새 배열을 돌려준다(원소 객체는 공유한다. ttl 감소는 호출자 몫).
 *
 * `list.length >= max` 이면:
 *   1) 섭취 이펙트(링이 아닌 것)부터 **오래된 순**으로 1장 밀어내고 새 이펙트를 붙인다.
 *   2) 밀어낼 섭취 이펙트가 없다(= 남은 것이 링뿐) → 링을 **전량 폐기**하고 새 이펙트만 남긴다.
 *      링 중 일부만 남기는 경로는 존재하지 않는다.
 *
 * 링(`relocate`) 자체를 이 함수로 넣으면 상한과 무관하게 뒤에 붙는다 — 부분 링을 만들 수 있는 경로를
 * 남기지 않기 위해서다. 링은 `ringEffects` 로 통째로 설치하는 것이 계약이다.
 */
export function pushEffect(
  list: readonly Effect[],
  fx: Effect,
  max: number = EFFECT_MAX,
): Effect[] {
  if (isRing(fx) || list.length < max) return [...list, fx];
  const victim = list.findIndex((f) => !isRing(f));
  if (victim < 0) return [fx];
  const out: Effect[] = [];
  for (let i = 0; i < list.length; i++) if (i !== victim) out.push(list[i] as Effect);
  out.push(fx);
  return out;
}
